import {
    Cancel,
    CheckCircle,
    CloudDownload,
    CloudUpload,
    Download,
    SaveAltOutlined,
    UploadFile,
} from "@mui/icons-material";
import {Alert, Box, Button, Snackbar} from "@mui/material";
import Grid from "@mui/material/Grid";
import {
    DataGrid,
    getGridNumericOperators,
    getGridStringOperators,
    GridFilterInputBoolean,
    GridFilterInputValue,
    GridToolbarColumnsButton,
    GridToolbarContainer,
    GridToolbarFilterButton,
} from "@mui/x-data-grid";
import {useEffect, useMemo, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import PageHeader from "../../components/PageHeader";
import {REP_TYPES} from "../../constants";
import {useLocalStorage} from "../../hooks/useLocalStorage";
import {useRetailLocationDownloadCsv} from "../../hooks/useRetailLocationDownloadCsv";
import {useRetailLocations} from "../../hooks/useRetailLocations";
import routes from "../../routes";
import {getCurrentUser} from "../../util/AuthUtil";
import RetailLocationModal from "./RetailLocationModal";
import RetailLocationsFilter from "./RetailLocationsFilter";
import RetailLocationImportModal from "./RetailLocationImportModal";
import {getInactiveRowClassName, inactiveRowStyles} from "../../util/DataGridUtil";
import {disableSortingAndMenu} from "../../util/FilterOperatorsUtil";

const FILTER_COLUMNS = {
    id: "ID",
    shiptoId: "SHIPTO_ID",
    displayName: "DISPLAY_NAME",
    street: "STREET",
    city: "CITY",
    state: "STATE",
    postalCode: "POSTAL_CODE",
    countryCode: "COUNTRY_CODE",
    onWebsite: "ON_WEBSITE",
};

const RetailLocationsTable = () => {
    const [filter, setFilter] = useState({
        businessUnitCode: "",
        active: "",
        selectFitEbsCodeId: "",
        selectFitCategoryId: "",
        filterColumn: "",
        filterValue: "",
        filterOperator: "",
        searchString: "",
    });
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 20,
    });

    const retailLocationModalRef = useRef();
    const retailLocationImportModalRef = useRef();

    const navigate = useNavigate();
    const currentUser = getCurrentUser();

    useEffect(() => {
        if (
            !routes.find((route) => route.key === "retail-locations").visibleRepTypes.includes(currentUser.repType.id)
        ) {
            navigate(REP_TYPES[currentUser.repType.id].defaultRoute);
        }
    }, [currentUser, navigate]);

    const [columnWidths, setColumnWidths] = useLocalStorage("retailLocationsTableColumnWidths", {});

    const gridNumericOperators = getGridNumericOperators();
    const gridStringOperators = getGridStringOperators();

    const textFilterOperators = [
        {
            label: "Contains",
            value: "CONTAINS",
            InputComponent: GridFilterInputValue,
            InputComponentProps: {
                type: "text",
            },
        },
        {
            label: "Does not contain",
            value: "DOES_NOT_CONTAIN",
            InputComponent: GridFilterInputValue,
            InputComponentProps: {
                type: "text",
            },
        },
        {
            label: "Equal to",
            value: "EQUAL_TO",
            InputComponent: GridFilterInputValue,
            InputComponentProps: {
                type: "text",
            },
        },
        {
            label: "Not equal to",
            value: "NOT_EQUAL_TO",
            InputComponent: GridFilterInputValue,
            InputComponentProps: {
                type: "text",
            },
        },
    ];

    const numberFilterOperators = [
        {
            label: "=",
            value: "EQUAL_TO",
            InputComponent: GridFilterInputValue,
            InputComponentProps: {
                type: "number",
            },
        },
        {
            label: "!=",
            value: "NOT_EQUAL_TO",
            InputComponent: GridFilterInputValue,
            InputComponentProps: {
                type: "number",
            },
        },
        {
            label: ">",
            value: "GREATER_THAN",
            InputComponent: GridFilterInputValue,
            InputComponentProps: {
                type: "number",
            },
        },
        {
            label: ">=",
            value: "GREATER_THAN_OR_EQUAL_TO",
            InputComponent: GridFilterInputValue,
            InputComponentProps: {
                type: "number",
            },
        },
        {
            label: "<",
            value: "LESS_THAN",
            InputComponent: GridFilterInputValue,
            InputComponentProps: {
                type: "number",
            },
        },
        {
            label: "<=",
            value: "LESS_THAN_OR_EQUAL_TO",
            InputComponent: GridFilterInputValue,
            InputComponentProps: {
                type: "number",
            },
        },
    ];

    const booleanFilterOperators = [
        {
            label: "is",
            value: "IS",
            InputComponent: GridFilterInputBoolean,
            InputComponentProps: {
                type: "boolean",
            },
        },
    ];

    const columns = useMemo(() => {
        let header = [
            {
                field: "id",
                headerName: "ID",
                width: columnWidths.id ?? 70,
                type: "number",
                filterOperators: numberFilterOperators,
            },
            {
                field: "businessUnitCode",
                headerName: "BUSINESS UNIT",
                width: columnWidths.businessUnitCode ?? 130,
                type: "string",
                filterable: false,
            },
            {
                field: "shiptoId",
                headerName: "SHIPTO",
                width: columnWidths.shiptoId ?? 100,
                type: "string",
                filterOperators: textFilterOperators,
            },
            {
                field: "selectFitEbsCode",
                headerName: "SELECT FIT EBS CODE",
                width: columnWidths.selectFitEbsCode ?? 250,
                filterable: false,
                renderCell: (params) => {
                    return params.value ? params.value.name : "NA";
                },
                sortComparator: (v1, v2) => {
                    if (v1 === null) return 1;
                    if (v2 === null) return -1;
                    return v1.name.localeCompare(v2.name);
                },
            },
            {
                field: "selectFitCategory",
                headerName: "SELECT FIT CATEGORY",
                width: columnWidths.selectFitCategory ?? 250,
                filterable: false,
                renderCell: (params) => {
                    return params.value ? params.value.name : "NA";
                },
                sortComparator: (v1, v2) => {
                    if (v1 === null) return 1;
                    if (v2 === null) return -1;
                    return v1.name.localeCompare(v2.name);
                },
            },
            {
                field: "selectFitLogoType",
                headerName: "SELECT FIT LOGO TYPE",
                width: columnWidths.selectFitLogoType ?? 250,
                filterable: false,
                renderCell: (params) => {
                    return params.value ? params.value.name : "NA";
                },
                sortComparator: (v1, v2) => {
                    if (v1 === null) return 1;
                    if (v2 === null) return -1;
                    return v1.name.localeCompare(v2.name);
                },
            },
            {
                field: "displayName",
                headerName: "NAME",
                width: columnWidths.displayName ?? 100,
                filterOperators: textFilterOperators,
            },
            {
                field: "street",
                headerName: "STREET",
                width: columnWidths.street ?? 100,
                filterOperators: textFilterOperators,
            },
            {
                field: "city",
                headerName: "CITY",
                width: columnWidths.city ?? 100,
                filterOperators: textFilterOperators,
            },
            {
                field: "state",
                headerName: "STATE",
                width: columnWidths.state ?? 80,
                filterOperators: textFilterOperators,
            },
            {
                field: "postalCode",
                headerName: "POSTAL CODE",
                width: columnWidths.postalCode ?? 80,
                filterOperators: textFilterOperators,
            },
            {
                field: "countryCode",
                headerName: "COUNTRY",
                width: columnWidths.countryCode ?? 80,
                filterOperators: textFilterOperators,
            },
            {
                field: "onWebsite",
                headerName: "ON WEBSITE",
                width: columnWidths.onWebsite ?? 80,
                filterOperators: booleanFilterOperators,
                renderCell: (params) => (
                    <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                        {params.value ? <CheckCircle color="success" /> : <Cancel color="error" />}
                    </Box>
                ),
                sortComparator: (v1, v2) => {
                    if (v1 === null) return 1;
                    if (v2 === null) return -1;
                    return v1 - v2;
                },
            },
            {
                field: "bookingLink",
                headerName: "BOOKING LINK",
                width: columnWidths.bookingLink ?? 100,
                filterable: false,
            },
            {
                field: "active",
                headerName: "ACTIVE",
                width: columnWidths.active ?? 80,
                type: "boolean",
                filterable: false,
                renderCell: (params) => (
                    <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                        {params.value ? <CheckCircle color="success" /> : <Cancel color="error" />}
                    </Box>
                ),
                sortComparator: (v1, v2) => {
                    if (v1 === null) return 1;
                    if (v2 === null) return -1;
                    return v1 - v2;
                },
            },
            {
                field: "isRetailer",
                headerName: "IS RETAILER",
                width: columnWidths.isRetailer ?? 90,
                type: "boolean",
                filterable: false,
                renderCell: (params) => (
                    <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                        {params.value ? <CheckCircle color="success" /> : <Cancel color="error" />}
                    </Box>
                ),
                sortComparator: (v1, v2) => {
                    if (v1 === null) return 1;
                    if (v2 === null) return -1;
                    return v1 - v2;
                },
            },
            {
                field: "isFittingLocation",
                headerName: "IS FITTING LOCATION",
                width: columnWidths.isFittingLocation ?? 120,
                type: "boolean",
                filterable: false,
                renderCell: (params) => (
                    <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                        {params.value ? <CheckCircle color="success" /> : <Cancel color="error" />}
                    </Box>
                ),
                sortComparator: (v1, v2) => {
                    if (v1 === null) return 1;
                    if (v2 === null) return -1;
                    return v1 - v2;
                },
            },
            {
                field: "latitude",
                headerName: "LATITUDE",
                width: columnWidths.latitude ?? 100,
                filterable: false,
            },
            {
                field: "longitude",
                headerName: "LONGITUDE",
                width: columnWidths.longitude ?? 100,
                filterable: false,
            },
        ];
        disableSortingAndMenu(header);
        return header;
    }, []);

    const [retailLocations, totalRowCount, refetchRetailLocations] = useRetailLocations(
        paginationModel.page,
        paginationModel.pageSize,
        filter.businessUnitCode,
        filter.active,
        filter.selectFitEbsCodeId,
        filter.selectFitCategoryId,
        filter.filterColumn,
        filter.filterValue,
        filter.filterOperator,
        filter.searchString
    );

    const handleColumnResize = (params) => {
        setColumnWidths((prevWidths) => ({
            ...prevWidths,
            [params.colDef.field]: params.width,
        }));
    };

    const filterChangeFunctions = useMemo(() => ({
        businessUnitCode: (value) => {
            setFilter((prevState) => ({
                ...prevState,
                businessUnitCode: value,
            }));
        },
        active: (value) => {
            setFilter((prevState) => ({
                ...prevState,
                active: value,
            }));
        },
        selectFitEbsCodeId: (value) => {
            setFilter((prevState) => ({
                ...prevState,
                selectFitEbsCodeId: value,
            }));
        },
        selectFitCategoryId: (value) => {
            setFilter((prevState) => ({
                ...prevState,
                selectFitCategoryId: value,
            }));
        },
        searchString: (value) => {
            setFilter((prevState) => ({
                ...prevState,
                searchString: value,
            }));
        },
    }), []);

    function renderControlPanel() {
        return (
            <Box pt={1}>
                <Grid
                    container
                    spacing={1}
                    sx={{
                        display: "flex",
                        // justifyContent: 'center',
                        alignItems: "center",
                    }}
                >
                    <Grid item xs={12}>
                        <RetailLocationsFilter
                            filter={filter}
                            filterChangeFunctions={filterChangeFunctions}
                            setFilter={setFilter}
                        />
                    </Grid>
                    <Grid
                        item
                        sx={{
                            display: "flex",
                            justifyContent: "right",
                        }}
                    >
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={() => retailLocationModalRef.current.handleOpen()}
                        >
                            + New Retail Location
                        </Button>
                    </Grid>
                    <Grid
                        item
                        sx={{
                            display: "flex",
                            justifyContent: "right",
                        }}
                    >
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            startIcon={<CloudUpload />}
                            onClick={() => retailLocationImportModalRef.current.handleOpen()}
                        >
                            Import Retail Locations
                        </Button>
                    </Grid>
                    <Grid
                        item
                        sx={{
                            display: "flex",
                            justifyContent: "right",
                        }}
                    >
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            startIcon={<Download />}
                            disabled={isExportRetailLocationToCSV}
                            onClick={handleExportAllData}
                        >
                            {isExportRetailLocationToCSV ? "Exporting..." : "Export Retail Locations"}
                        </Button>
                    </Grid>
                </Grid>
            </Box>
        );
    }

    function handleRowClick(params) {
        retailLocationModalRef.current.handleOpen(params.row.id);
    }
    const [exportRetailLocationToCSV, isExportRetailLocationToCSV, isErrorExport] = useRetailLocationDownloadCsv(
        {
            businessUnitCode: filter.businessUnitCode,
            active: filter.active,
            selectFitEbsCodeId: filter.selectFitEbsCodeId,
            selectFitCategoryId: filter.selectFitCategoryId,
            filterColumn: filter.filterColumn,
            filterValue: filter.filterValue,
            filterOperator: filter.filterOperator,
            searchString: filter.searchString,
        },
        false
    );
    const [showErrorExportRetailLocations, setShowErrorExportRetailLocations] = useState(false);

    useEffect(() => {
        setShowErrorExportRetailLocations(isErrorExport);
    }, [isErrorExport]);

    const handleExportAllData = async () => {
        setShowErrorExportRetailLocations(false);
        exportRetailLocationToCSV();
    };
    function CustomToolbar() {
        return (
            <GridToolbarContainer>
                <GridToolbarColumnsButton />
                {/* <GridToolbarFilterButton /> */}
            </GridToolbarContainer>
        );
    }
    function onFilterChange(filterModel) {
        const filterItem = filterModel.items[0];
        if (filterItem) {
            const filterColumn = FILTER_COLUMNS[filterItem.field];
            const filterValue = filterItem.value;
            const filterOperator = filterItem.operator;
            // if (filterColumn && filterValue && filterOperator) {
            setFilter((prevState) => ({
                ...prevState,
                filterColumn: filterColumn,
                filterValue: filterValue,
                filterOperator: filterOperator,
            }));
            // }
        } else {
            // Reset filter
            setFilter((prevState) => ({
                ...prevState,
                filterColumn: "",
                filterValue: "",
                filterOperator: "",
            }));
        }
    }
    return (
        <>
            <Box
                sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <PageHeader title="Manage Retail Locations" children={renderControlPanel()} />

                <Box
                    sx={{
                        width: "100%",
                        height: "100%",
                        position: "relative",
                    }}
                >
                    <DataGrid
                        columns={columns}
                        rows={retailLocations}
                        filterMode="server"
                        filterDebounceMs={500}
                        onFilterModelChange={onFilterChange}
                        paginationMode="server"
                        rowCount={totalRowCount}
                        pageSizeOptions={[10, 20, 50]}
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        density="compact"
                        slots={{toolbar: CustomToolbar}}
                        onRowClick={handleRowClick}
                        disableRowSelectionOnClick={true}
                        onColumnWidthChange={handleColumnResize}
                        getRowClassName={getInactiveRowClassName("active")}
                        // disableColumnFilter={true}
                        sx={{
                            height: "100%",
                            position: "absolute",
                            width: "100%",
                            "& .MuiDataGrid-cell:focus": {
                                outline: "none",
                            },
                            ...inactiveRowStyles,
                            // fontSize: '1rem',
                        }}
                    />
                </Box>
                <RetailLocationModal ref={retailLocationModalRef} refetchRetailLocations={refetchRetailLocations} />
                <RetailLocationImportModal
                    ref={retailLocationImportModalRef}
                    refetchRetailLocations={refetchRetailLocations}
                />
                <Snackbar
                    open={showErrorExportRetailLocations}
                    autoHideDuration={6000}
                    onClose={() => setShowErrorExportRetailLocations(false)}
                    anchorOrigin={{vertical: "bottom", horizontal: "center"}}
                >
                    <Alert
                        onClose={() => setShowErrorExportRetailLocations(false)}
                        severity="error"
                        sx={{width: "100%"}}
                    >
                        Error export data to csv
                    </Alert>
                </Snackbar>
            </Box>
        </>
    );
};

export default RetailLocationsTable;
